/**
 * SolidJS wrapper for the TipTap editor (headless, no React bindings).
 *
 * It works identically on every Tauri platform: WebKitGTK (Linux),
 * WKWebView (macOS/iOS), WebView2 (Windows) and Android WebView — TipTap only
 * needs a DOM element to mount on.
 *
 * It assembles the full block experience:
 *   - StarterKit (incl. markdown shortcuts and dropcursor) + BlockId (1 block = 1 row)
 *   - Localized Placeholder
 *   - Slash menu "/" (block insertion)
 *   - Wiki-links "[[page]]" with autocomplete and creation on the fly
 *   - Floating format toolbar over the selection
 *   - Block drag & drop with handle ⠿
 */
import { Editor, type JSONContent } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { BubbleMenu } from "@tiptap/extension-bubble-menu";
import { DragHandle } from "@tiptap/extension-drag-handle";
import { TextSelection } from "@tiptap/pm/state";
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createPageMutation } from "@apunte-digital/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { BlockId } from "../lib/block-id";
import {
  buildSlashMenuItems,
  createSlashMenuExtension,
  type SlashMenuItem,
} from "../lib/slash-menu";
import { createWikiLinkExtension, type WikiLinkSuggestionItem } from "../lib/wiki-link";
import { createSuggestionRenderer } from "../lib/suggestion-menu";
import { usePageTree } from "../hooks/use-page-tree";
import { SelectionToolbar } from "./selection-toolbar";

interface PageEditorProps {
  /** Initial document (built from the block rows). */
  initialDoc: JSONContent;
  /** Fired on every local change; the hook decides when to persist. */
  onDocUpdate: (editor: Editor) => void;
  /** Hands over the instance to apply remote changes / cleanup flush. */
  onReady: (editor: Editor) => void;
  editable?: boolean;
}

export function PageEditor(props: PageEditorProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { pages } = usePageTree();

  let host: HTMLDivElement | undefined;
  let toolbarElement: HTMLDivElement | undefined;
  const [editorInstance, setEditorInstance] = createSignal<Editor | null>(null);

  // ── Wiki-links: registry of NodeViews that refresh their title ───────────
  const refreshCallbacks = new Set<() => void>();
  createEffect(() => {
    void pages.data;
    for (const callback of refreshCallbacks) {
      callback();
    }
  });

  // TipTap extensions are created once (non-reactive framework). Labels are
  // evaluated at component mount; locale is already loaded by then.
  // i18n-reactive-ok for all t() calls below that feed into TipTap extensions.
  const wikiLink = createWikiLinkExtension({
    getPages: () =>
      (pages.data ?? []).map((page) => ({ id: page.id, title: page.title, icon: page.icon })),
    navigate: (pageId) => {
      void navigate({ to: "/notes/$pageId", params: { pageId } });
    },
    createPage: async (title) => {
      const dbInstance = drizzle();
      const uid = userId();
      if (!dbInstance || !uid) {
        return null;
      }
      return createPageMutation(dbInstance, uid, { title });
    },
    registerRefresh: (callback) => {
      refreshCallbacks.add(callback);
      return () => {
        refreshCallbacks.delete(callback);
      };
    },
    untitledLabel: t(msg`Untitled`), // i18n-reactive-ok — TipTap is not reactive; label is evaluated at render (locale already loaded)
    render: createSuggestionRenderer<WikiLinkSuggestionItem>({
      renderItem: (item) => (
        <span class="suggestion-menu-row">
          <Show
            when={!item.isCreate}
            fallback={
              <span class="suggestion-menu-label">
                {t(msg`Create page`)} "{item.title}"
              </span>
            }
          >
            <span class="suggestion-menu-icon">{item.icon ?? "📄"}</span>
            <span class="suggestion-menu-label">{item.title}</span>
          </Show>
        </span>
      ),
    }),
  });

  // ── Slash menu ────────────────────────────────────────────────────────────
  const slashItems = buildSlashMenuItems({
    text: t(msg`Text`), // i18n-reactive-ok
    heading1: t(msg`Heading 1`), // i18n-reactive-ok
    heading2: t(msg`Heading 2`), // i18n-reactive-ok
    heading3: t(msg`Heading 3`), // i18n-reactive-ok
    bulletList: t(msg`Bullet list`), // i18n-reactive-ok
    orderedList: t(msg`Numbered list`), // i18n-reactive-ok
    quote: t(msg`Quote`), // i18n-reactive-ok
    codeBlock: t(msg`Code block`), // i18n-reactive-ok
    divider: t(msg`Divider`), // i18n-reactive-ok
    hints: {
      text: t(msg`Plain paragraph`), // i18n-reactive-ok
      heading1: t(msg`Large section heading`), // i18n-reactive-ok
      heading2: t(msg`Medium section heading`), // i18n-reactive-ok
      heading3: t(msg`Small section heading`), // i18n-reactive-ok
      bulletList: t(msg`Simple bulleted list`), // i18n-reactive-ok
      orderedList: t(msg`List with numbering`), // i18n-reactive-ok
      quote: t(msg`Capture a quote`), // i18n-reactive-ok
      codeBlock: t(msg`Code snippet with monospace font`), // i18n-reactive-ok
      divider: t(msg`Visual separator`), // i18n-reactive-ok
    },
  });

  const slashMenu = createSlashMenuExtension({
    items: slashItems,
    render: createSuggestionRenderer<SlashMenuItem>({
      renderItem: (item) => (
        <span class="suggestion-menu-row">
          <span class="suggestion-menu-label">{item.label}</span>
          <span class="suggestion-menu-hint">{item.hint}</span>
        </span>
      ),
      emptyLabel: t(msg`No results`), // i18n-reactive-ok
    }),
  });

  onMount(() => {
    if (!host || !toolbarElement) {
      return;
    }
    const editor = new Editor({
      element: host,
      extensions: [
        StarterKit,
        BlockId,
        Placeholder.configure({
          placeholder: t(msg`Write something, or press '/' for commands…`), // i18n-reactive-ok
        }),
        slashMenu,
        wikiLink,
        BubbleMenu.configure({
          element: toolbarElement,
          pluginKey: "notes-selection-toolbar",
          shouldShow: ({ state }) => {
            const { selection } = state;
            return !selection.empty && selection instanceof TextSelection;
          },
          options: { placement: "top", offset: 8 },
        }),
        DragHandle.configure({
          render: () => {
            const handle = document.createElement("div");
            handle.className = "notes-drag-handle";
            handle.textContent = "⠿";
            return handle;
          },
        }),
      ],
      content: props.initialDoc,
      editable: props.editable ?? true,
      editorProps: {
        attributes: {
          class: "notes-editor focus:outline-none",
        },
      },
      onUpdate: ({ editor: instance }) => {
        props.onDocUpdate(instance);
      },
    });

    setEditorInstance(editor);
    props.onReady(editor);

    onCleanup(() => {
      editor.destroy();
    });
  });

  return (
    <>
      <SelectionToolbar
        editor={editorInstance}
        setElement={(element) => {
          toolbarElement = element;
        }}
      />
      {/* data-no-swipe: the contenteditable must not start the carousel between
          notas — el arrastre dentro del editor es para seleccionar texto. */}
      <div ref={host} data-no-swipe="true" class="notes-editor-host flex-1 min-h-0" />
    </>
  );
}
